import EditIcon from '@material-ui/icons/EditOutlined';
import { MapOutlined, MonetizationOn, AccountBalanceWallet, Description, Receipt, Business, ExpandMore, ExpandLess } from '@material-ui/icons';
import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import AddMapping from "./AddMapping";
import fetchClient from "../../api/fetchClient";

const documentTypes = [
	{
		label: 'Nota Fiscal de Entrada',
		value: 'nfe',
		docCode: '18',
		icon: <Receipt />,
		color: '#3498db',
	},
	{
		label: 'Lançamento Contábil',
		value: 'lc',
		docCode: '30',
		icon: <Description />,
		color: '#9b59b6',
	}
];

const transactionTypes = [
	{
		label: 'Expense',
		value: 'expense',
		icon: <MonetizationOn />,
		color: '#27ae60',
	},
	{
		label: 'Cash',
		value: 'cash',
		icon: <AccountBalanceWallet />,
		color: '#f39c12',
	}
];

function TabMapeamento({ values, errors, handleChange, setFieldValue, touched, initialValues }) {
	const [showAdd, setShowAdd] = useState(false);
	const [addValue, setAddValue] = useState(null);
	const [expandedCompanies, setExpandedCompanies] = useState([]);
	const [empresaOptions, setEmpresaOptions] = useState([]);

	const empresa = values.empresa || [];

	useEffect(() => {
		const fetchEmpresas = async () => {
			try {
				let allData = [];
				let nextToken = null;
				do {
					const { data } = await fetchClient.get(`getAllAplicacao?limit=100${nextToken ? `&nextToken=${nextToken}` : ''}`);
					allData = [...allData, ...data.value];
					nextToken = data.nextToken;
				} while (nextToken);
				setEmpresaOptions(allData.map(app => ({ value: app.id, label: app.description })));
			} catch (error) {
				console.error("Erro ao buscar empresas:", error);
			}
		};
		fetchEmpresas();
	}, []);

	// Expande a primeira empresa por padrão
	useEffect(() => {
		if (empresa.length > 0 && expandedCompanies.length === 0) {
			setExpandedCompanies([0]);
		}
	}, [empresa.length]);

	const toggleExpand = (index) => {
		setExpandedCompanies(prev =>
			prev.includes(index)
				? prev.filter(i => i !== index)
				: [...prev, index]
		);
	};

	const findBusinessName = (id) => {
		const app = empresaOptions.find(app => app.value === id);
		return app ? app.label : id || 'Empresa sem nome';
	};

	const handleEditMapping = (empIndex, docType, transType) => {
		const mappingKey = `mapping-${docType.value}-${transType.value}`;
		const fieldPath = `empresa[${empIndex}].${mappingKey}`;
		const mappingValues = empresa[empIndex]?.[mappingKey] || [];

		setAddValue({
			fieldPath,
			values: mappingValues,
		});
		setShowAdd(true);
	};

	const getMappingCounts = (mapping) => {
		let headerCount = 0;
		let linesCount = 0;
		let totalCount = 0;

		if (Array.isArray(mapping)) {
			totalCount = mapping.length;
			headerCount = mapping.length;
		} else if (mapping && typeof mapping === 'object') {
			headerCount = mapping.header?.length || 0;
			linesCount = mapping.lines?.length || 0;
			totalCount = headerCount + linesCount;
		}

		return { headerCount, linesCount, totalCount };
	};

	return (
		<ModernFormContainer>
			{showAdd && addValue ? (
				<AddMapping
					show={setShowAdd}
					type={addValue.fieldPath}
					values={addValue.values}
					setFieldValue={setFieldValue}
				/>
			) : null}

			{empresa.length === 0 ? (
				<EmptyStateCard>
					<Business style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '12px' }} />
					<EmptyTitle>Nenhuma empresa cadastrada</EmptyTitle>
					<EmptySubtitle>Adicione empresas na aba "Empresas" para configurar os mapeamentos</EmptySubtitle>
				</EmptyStateCard>
			) : (
				empresa.map((emp, empIndex) => {
					const isExpanded = expandedCompanies.includes(empIndex);
					const empresaName = findBusinessName(emp.empresa);

					// Contar total de regras da empresa
					let totalRulesEmpresa = 0;
					documentTypes.forEach(docType => {
						transactionTypes.forEach(transType => {
							const mappingKey = `mapping-${docType.value}-${transType.value}`;
							const { totalCount } = getMappingCounts(emp[mappingKey]);
							totalRulesEmpresa += totalCount;
						});
					});

					return (
						<CompanyCard key={empIndex}>
							<CompanyHeader onClick={() => toggleExpand(empIndex)} isExpanded={isExpanded}>
								<CompanyHeaderLeft>
									<CompanyIcon>
										<Business />
									</CompanyIcon>
									<CompanyInfo>
										<CompanyName>{empresaName}</CompanyName>
										<CompanyMeta>
											{totalRulesEmpresa > 0 ? (
												<CompanyBadge active>{totalRulesEmpresa} {totalRulesEmpresa === 1 ? 'regra' : 'regras'} configuradas</CompanyBadge>
											) : (
												<CompanyBadge>Sem mapeamentos</CompanyBadge>
											)}
										</CompanyMeta>
									</CompanyInfo>
								</CompanyHeaderLeft>
								<ExpandIconWrapper>
									{isExpanded ? <ExpandLess /> : <ExpandMore />}
								</ExpandIconWrapper>
							</CompanyHeader>

							{isExpanded && (
								<CompanyContent>
									{documentTypes.map((docType, docIndex) => (
										<SectionCard key={docIndex}>
											<SectionHeader>
												<SectionIcon color={docType.color}>
													{docType.icon}
												</SectionIcon>
												<div>
													<SectionTitle>{docType.label}</SectionTitle>
													<SectionSubtitle>Código do Documento: {docType.docCode}</SectionSubtitle>
												</div>
											</SectionHeader>

											<TableWrapper>
												<ModernTable>
													<thead>
														<TableHeaderRow>
															<TableHead>Tipo de Transação</TableHead>
															<TableHead align="center">Regras Cadastradas</TableHead>
															<TableHead align="right">Ações</TableHead>
														</TableHeaderRow>
													</thead>
													<tbody>
														{transactionTypes.map((transType, transIndex) => {
															const mappingKey = `mapping-${docType.value}-${transType.value}`;
															const mapping = emp[mappingKey];
															const { headerCount, linesCount, totalCount } = getMappingCounts(mapping);

															return (
																<TableRow key={transIndex}>
																	<TableCell>
																		<TypeInfo>
																			<TypeIcon color={transType.color}>
																				{transType.icon}
																			</TypeIcon>
																			<TypeLabel>{transType.label}</TypeLabel>
																		</TypeInfo>
																	</TableCell>
																	<TableCell align="center">
																		<RulesBadgeContainer>
																			<RulesBadge count={totalCount}>
																				{totalCount} {totalCount === 1 ? 'regra' : 'regras'}
																			</RulesBadge>
																			{totalCount > 0 && (
																				<RulesBreakdown>
																					<BreakdownItem>
																						<BreakdownLabel>Header:</BreakdownLabel>
																						<BreakdownValue>{headerCount}</BreakdownValue>
																					</BreakdownItem>
																					<BreakdownItem>
																						<BreakdownLabel>Linhas:</BreakdownLabel>
																						<BreakdownValue>{linesCount}</BreakdownValue>
																					</BreakdownItem>
																				</RulesBreakdown>
																			)}
																		</RulesBadgeContainer>
																	</TableCell>
																	<TableActions>
																		<ActionButton
																			onClick={(e) => {
																				e.preventDefault();
																				handleEditMapping(empIndex, docType, transType);
																			}}
																		>
																			<EditIcon style={{ marginRight: '6px', fontSize: '18px' }} />
																			Editar
																		</ActionButton>
																	</TableActions>
																</TableRow>
															);
														})}
													</tbody>
												</ModernTable>
											</TableWrapper>
										</SectionCard>
									))}
								</CompanyContent>
							)}
						</CompanyCard>
					);
				})
			)}
		</ModernFormContainer>
	);
}

export default TabMapeamento;

// Animações
const fadeIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

const ModernFormContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
	font-family: 'Mulish', sans-serif;
`;

const CompanyCard = styled.div`
	background: white;
	border-radius: 16px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
	border: 1px solid #e8e8e8;
	animation: ${fadeIn} 0.5s ease;
	transition: all 0.3s ease;
	overflow: hidden;

	&:hover {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
		border-color: #d0d0d0;
	}
`;

const CompanyHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 20px 24px;
	cursor: pointer;
	transition: all 0.2s ease;
	background: ${props => props.isExpanded ? '#f8f9fa' : 'white'};
	border-bottom: ${props => props.isExpanded ? '2px solid #e5e7eb' : 'none'};

	&:hover {
		background: #f8f9fa;
	}
`;

const CompanyHeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`;

const CompanyIcon = styled.div`
	width: 44px;
	height: 44px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(142, 68, 173, 0.1);
	color: #8e44ad;

	svg {
		font-size: 24px;
	}
`;

const CompanyInfo = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const CompanyName = styled.h3`
	font-size: 16px;
	font-weight: 700;
	color: #2c3e50;
	margin: 0;
	font-family: 'Mulish', sans-serif;
`;

const CompanyMeta = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const CompanyBadge = styled.span`
	font-size: 12px;
	font-weight: 600;
	padding: 3px 10px;
	border-radius: 12px;
	font-family: 'Mulish', sans-serif;
	${props => props.active
		? `
			background: rgba(39, 174, 96, 0.1);
			color: #27ae60;
		`
		: `
			background: rgba(149, 165, 166, 0.1);
			color: #95a5a6;
		`
	}
`;

const ExpandIconWrapper = styled.div`
	color: #7f8c8d;
	display: flex;
	align-items: center;
	transition: transform 0.2s ease;
`;

const CompanyContent = styled.div`
	padding: 24px;
	display: flex;
	flex-direction: column;
	gap: 20px;
`;

const SectionCard = styled.div`
	background: #fafbfc;
	border-radius: 12px;
	padding: 20px;
	border: 1px solid #eef2f7;
`;

const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 16px;
	padding-bottom: 12px;
	border-bottom: 2px solid #eef2f7;
`;

const SectionIcon = styled.div`
	width: 36px;
	height: 36px;
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${props => props.color ? `${props.color}15` : '#667eea15'};
	color: ${props => props.color || '#667eea'};
	margin-right: 12px;

	svg {
		font-size: 20px;
	}
`;

const SectionTitle = styled.h3`
	font-size: 16px;
	font-weight: 700;
	color: #2c3e50;
	margin: 0;
	font-family: 'Mulish', sans-serif;
`;

const SectionSubtitle = styled.p`
	font-size: 12px;
	font-weight: 500;
	color: #7f8c8d;
	margin: 2px 0 0 0;
	font-family: 'Mulish', sans-serif;
`;

const TableWrapper = styled.div`
	width: 100%;
	overflow-x: auto;
`;

const ModernTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	min-width: 500px;
`;

const TableHeaderRow = styled.tr`
	background: #f0f2f5;
	border-radius: 8px;
`;

const TableHead = styled.th`
	text-align: ${props => props.align || 'left'};
	padding: 10px 16px;
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	border-bottom: 2px solid #e5e7eb;
	font-family: 'Mulish', sans-serif;
`;

const TableRow = styled.tr`
	border-bottom: 1px solid #eef2f7;
	transition: all 0.2s ease;

	&:hover {
		background: #f9fbff;
	}
`;

const TableCell = styled.td`
	padding: 14px 16px;
	color: #4c5968;
	font-size: 14px;
	font-family: 'Mulish', sans-serif;
	text-align: ${props => props.align || 'left'};
`;

const TypeInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
`;

const TypeIcon = styled.div`
	width: 32px;
	height: 32px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${props => props.color ? `${props.color}15` : '#667eea15'};
	color: ${props => props.color || '#667eea'};

	svg {
		font-size: 18px;
	}
`;

const TypeLabel = styled.span`
	font-size: 14px;
	font-weight: 600;
	color: #2c3e50;
	font-family: 'Mulish', sans-serif;
`;

const RulesBadgeContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
`;

const RulesBadge = styled.div`
	display: inline-flex;
	align-items: center;
	padding: 4px 12px;
	border-radius: 16px;
	font-size: 12px;
	font-weight: 600;
	font-family: 'Mulish', sans-serif;
	${props => props.count > 0
		? `
			background: rgba(39, 174, 96, 0.1);
			color: #27ae60;
		`
		: `
			background: rgba(149, 165, 166, 0.1);
			color: #95a5a6;
		`
	}
`;

const RulesBreakdown = styled.div`
	display: flex;
	gap: 10px;
	font-size: 11px;
	font-family: 'Mulish', sans-serif;
`;

const BreakdownItem = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
`;

const BreakdownLabel = styled.span`
	color: #7f8c8d;
	font-weight: 500;
`;

const BreakdownValue = styled.span`
	color: #2c3e50;
	font-weight: 700;
	background: rgba(52, 152, 219, 0.1);
	padding: 1px 5px;
	border-radius: 4px;
`;

const TableActions = styled.td`
	padding: 14px 16px;
	text-align: right;
	display: flex;
	gap: 8px;
	justify-content: flex-end;
	align-items: center;
`;

const ActionButton = styled.button`
	border: none;
	border-radius: 8px;
	padding: 7px 14px;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	transition: all 0.3s ease;
	font-family: 'Mulish', sans-serif;
	background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
	color: white;
	box-shadow: 0 2px 6px rgba(52, 152, 219, 0.3);

	&:hover {
		box-shadow: 0 4px 10px rgba(52, 152, 219, 0.4);
		transform: translateY(-1px);
	}

	&:active {
		transform: translateY(0);
	}
`;

const EmptyStateCard = styled.div`
	background: white;
	border-radius: 16px;
	padding: 64px 24px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
	border: 1px solid #e8e8e8;
	text-align: center;
	animation: ${fadeIn} 0.5s ease;
`;

const EmptyTitle = styled.h3`
	font-size: 18px;
	font-weight: 700;
	color: #2c3e50;
	margin: 0 0 8px 0;
	font-family: 'Mulish', sans-serif;
`;

const EmptySubtitle = styled.p`
	font-size: 14px;
	color: #7f8c8d;
	margin: 0;
	font-family: 'Mulish', sans-serif;
`;
